import { createSelector } from 'reselect';
import { createFeatureSelector } from 'common/create-feature-selector';
import { getEnv, getExperimentsDomain } from 'common/selectors/env';
import { RootState, Dispatch } from 'store';
import { getDomain, getHostname as getHostnameUtil, getRegion, getQaCookies, getLang } from 'utils';
import { FetchModelProps } from 'features/page-model/types';
import { getDomainFromHeader } from 'entries/server/utils';
import { getHostname } from 'common/selectors/location';
import { fetchModelThunk } from './page-model-slice';
import { GetModelParams } from './api';
import { experimentsConfig, SHOW_STOREFRONT_PICS_INTERNAL_ID } from '../../../utils/config-experiments';
import { useConfigurableExperiment } from '@cft/react-experiments';

export const rootSelector = createFeatureSelector('pageModel');
export const getModelJson = createSelector(rootSelector, (s) => s.data);
export const getBreadcrumbs = createSelector(getModelJson, (m) => m?.breadcrumbs);
export const getSeoDescription = createSelector(getModelJson, (m) => m?.seoProperties.seotext);
export const getAnalytics = createSelector(getModelJson, (m) => m?.analytics || {});
export const getSeoProperties = createSelector(getModelJson, (m) => m?.seoProperties);
export const getRegionId = createSelector(getModelJson, (m) => m?.properties.regionId);
export const getWarningMessage = createSelector(getModelJson, (m) => m?.warningMessage);
export const getModelErrorCode = createSelector(rootSelector, (s) => s.errorCode);
export const getTitle = createSelector(getModelJson, (m) => m?.properties.title);
export const getRegions = createSelector(getModelJson, (m) => m?.regions || []);

const mainSelector = (state: RootState) => state;

export const getExperiments = createSelector(getModelJson, (m) => m?.experiments || []);
export const createGetExperiment = (experimentInternalID: string) =>
  createSelector(
    getExperiments,
    (experiments) => experiments?.find(({ internalId }) => internalId === experimentInternalID) ?? null,
  );
export const getExperimentsUUID = createSelector(mainSelector, (store: RootState) => store.uuid);

export type ABOnReturnType = {
  on: boolean;
  variant: string | null;
  name: string;
};

export const createGetExperimentOn = (experimentName: string) =>
  createSelector(
    [getExperimentsUUID, createGetExperiment(experimentName), getHostname, getExperimentsDomain],
    (uuid, experimentFromFudzi, host, experimentsDomain): ABOnReturnType => {
      const { variant, isActive } = experimentFromFudzi
        ? useConfigurableExperiment(
            experimentName,
            uuid,
            experimentsConfig,
            getDomainFromHeader(experimentsDomain || host),
          )
        : { variant: null, isActive: false };

      return {
        on: isActive,
        variant,
        name: experimentName,
      };
    },
  );

export const getShowStorefrontPics = createGetExperiment(SHOW_STOREFRONT_PICS_INTERNAL_ID);

export const fetchModel = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const hostname = getHostnameUtil(state.host || '');
  const qaCookie = getQaCookies(state.cookie);
  const domain = getDomain(hostname);
  const region = getRegion(hostname, domain);
  const lang = getLang();
  const { ORCHESTRATOR_HOST = '', API_KEY = '' } = getEnv(state);
  const pathname = state.router?.path;
  const params: FetchModelProps = {
    pathname,
    region,
    lang,
  };
  const options: GetModelParams = {
    host: ORCHESTRATOR_HOST,
    params,
    config: {
      headers: {
        'x-api-key': API_KEY,
      },
    },
  };

  if (qaCookie) {
    options.config = { ...options.config, headers: { ...options.config?.headers, cookie: qaCookie } };
  }

  return dispatch(fetchModelThunk(options));
};
